import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Link,
  Modal,
  Backdrop,
  Fade
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import "./../../index.css";

const Register = () => {
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    email: ""
  });
  const [aceptaPoliticas, setAceptaPoliticas] = useState(false);
  const [error, setError] = useState("");
  const [csrfToken, setCsrfToken] = useState("");
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Registro | D&R Soluciones Informáticas";
    window.scrollTo(0, 0);
    obtenerCsrfToken();
  }, []);

  const obtenerCsrfToken = async () => {
    try {
      const res = await fetch("/sistema/get-csrf-token/", {
        method: "GET",
        credentials: "include"
      });
      const data = await res.json();
      setCsrfToken(data.csrfToken);
    } catch {
      setCsrfToken("");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre || !formData.telefono || !formData.email) {
      setError("Todos los campos son obligatorios.");
      return;
    }

    if (!aceptaPoliticas) {
      setError("Debe aceptar las políticas de privacidad para continuar.");
      return;
    }

    if (!csrfToken) {
      setError("No se pudo obtener el token CSRF.");
      return;
    }

    setError("");
    setSuccessModalOpen(true); 

    try {
      const response = await fetch("/sistema/register/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken
        },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.status !== 201) {
        setSuccessModalOpen(false); 
        setError(data.error || "No se pudo registrar el usuario.");
      }
    } catch {
      setSuccessModalOpen(false);
      setError("Error de conexión con el servidor.");
    }
  };

  const handleCloseModal = () => {
    setSuccessModalOpen(false);
    navigate("/login");
  };

  const inputStyle = {
    sx: {
      "& input": {
        color: "#000",
        WebkitTextFillColor: "#000 !important",
        transition: "background-color 5000s ease-in-out 0s"
      },
      "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
          borderColor: "#1976d2"
        }
      }
    }
  };

  return (
    <Box sx={{ bgcolor: "#fff", minHeight: "100vh", py: 6 }}>
      <Container maxWidth="sm">
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.5rem" },
              fontWeight: "bold",
              color: "#222",
              mb: 2
            }}
          >
            Registro de Usuario
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.95rem", sm: "1rem" },
              color: "#444",
              maxWidth: "600px",
              mx: "auto",
              textAlign: "center",
              lineHeight: 1.6
            }}
          >
            Regístrate para acceder a <strong>descuentos exclusivos</strong> en tus compras.
            Si eres un cliente frecuente, podrás disfrutar de <strong>promociones personalizadas</strong>,
            <strong> beneficios únicos</strong> y atención preferencial. ¡Forma parte de nuestra comunidad!
          </Typography>
        </Box>

        {error && (
          <Typography color="error" align="center" mb={2}>
            {error}
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Nombre"
            name="nombre"
            fullWidth
            margin="normal"
            value={formData.nombre}
            onChange={handleChange}
            InputProps={{
              ...inputStyle,
              endAdornment: formData.nombre && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setFormData({ ...formData, nombre: "" })}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            InputLabelProps={{ sx: { color: "#000" } }}
          />

          <TextField
            label="Teléfono"
            name="telefono"
            fullWidth
            margin="normal"
            value={formData.telefono}
            onChange={handleChange}
            InputProps={{
              ...inputStyle,
              endAdornment: formData.telefono && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setFormData({ ...formData, telefono: "" })}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            InputLabelProps={{ sx: { color: "#000" } }}
          />

          <TextField
            label="Correo Electrónico"
            name="email"
            type="email"
            fullWidth
            margin="normal"
            value={formData.email}
            onChange={handleChange}
            InputProps={{
              ...inputStyle,
              endAdornment: formData.email && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setFormData({ ...formData, email: "" })}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            InputLabelProps={{ sx: { color: "#000" } }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={aceptaPoliticas}
                onChange={(e) => setAceptaPoliticas(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                He leído y acepto las{" "}
                <Link
                  href="/politicas-privacidad"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                >
                  políticas de privacidad
                </Link>
              </Typography>
            }
            sx={{ mt: 2 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              backgroundColor: "#006064",
              fontWeight: "bold",
              fontSize: { xs: "0.95rem", sm: "1rem" },
              "&:hover": {
                backgroundColor: "#004d4d"
              }
            }}
          >
            REGISTRARME
          </Button>
        </form>
      </Container>

      <Modal
        open={successModalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={successModalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              borderRadius: 3,
              boxShadow: 24,
              p: 4,
              maxWidth: 400,
              width: "90%",
              textAlign: "center"
            }}
          >
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              ¡Registro exitoso!
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              Tu cuenta ha sido registrada correctamente. <br />
              En breve recibirás un correo con tu <strong>clave temporal</strong> para iniciar sesión.
            </Typography>
            <Button
              onClick={handleCloseModal}
              variant="contained"
              sx={{
                backgroundColor: "#006064",
                "&:hover": { backgroundColor: "#004d4d" },
                fontWeight: "bold",
                px: 3
              }}
            >
              IR AL LOGIN
            </Button>
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
};

export default Register;
